import { useWindowHeight } from '@react-hook/window-size'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'

import PortalMuteButton from '~/components/PortalMuteButton'
import appConfig from '~/config/app'
import ROUTES from '~/shared/constants/routes.const'
import { getFirebase } from '~/firebase'
import { MuteElement, UnMuteElement } from '~/math-vault/components/MathVaultSoundMuteIcons'
import useChestIdsMapped from '~/math-vault/hooks/useChestIdsMapped'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectMathVaultLocation, setCurrentLocationCreator } from '~/redux/slices/math-vault'
import { selectUserId } from '~/auth/slices/auth.slice'
import { selectMuted, selectVolume } from '~/redux/slices/sound-control'
import { Ii18nLocales } from '~/types/i18n'
import { VaultSpawnLocations } from '~/types/math-vault'
import { Breakpoint, useBreakpoint } from '~/utils/useBreakpoint'
import playCanvasAnalytics from '~/analytics/playcanvas/playcanvasAnalyticsEventLogger'
import { DB_COLLECTION } from '~/math-vault/constants/db.const'

interface openChest {
  elevator: string
  room: string
  chest: string
  location: VaultSpawnLocations
}

export function onClickBackButton(message: Record<string, string>): void {
  const vaultFrame = document.getElementsByClassName('vaultFrame')[0] as HTMLIFrameElement
  const postMessage = vaultFrame?.contentWindow

  if (!postMessage) return
  postMessage.postMessage(message, appConfig.playcanvas.mathVault.origin)
}

export default function MathVaultIntro({
  setShowBackButton,
  playLocales,
}: {
  setShowBackButton: Dispatch<SetStateAction<boolean>>
  playLocales: Ii18nLocales
}): React.ReactElement {
  const router = useRouter()
  const language = router.locale ? router.locale : null
  const br = useBreakpoint()
  const mathVaultLocation = useAppSelector(selectMathVaultLocation)
  const dispatch = useAppDispatch()
  const { chestIdMapped } = useChestIdsMapped()
  const userId = useAppSelector(selectUserId)
  const height = useWindowHeight()
  const [isReady, setIsReady] = useState(false)
  const globallyMuted = useAppSelector(selectMuted)
  const globalVolume = useAppSelector(selectVolume)

  const sendMessage = useCallback((message: Record<string, unknown | null>): void => {
    const vaultFrame = document.getElementsByClassName('vaultFrame')[0] as HTMLIFrameElement
    const postMessage = vaultFrame?.contentWindow

    if (!postMessage) return
    postMessage.postMessage(message, appConfig.playcanvas.mathVault.origin)
  }, [])

  const handleEvent = useCallback(
    async (e: openChest) => {
      let chestData = null

      if (userId) {
        const chestPath = [
          DB_COLLECTION.MATH_VAULT,
          String(userId),
          DB_COLLECTION.STATE,
          e.elevator,
          DB_COLLECTION.FLOORS,
          e.room,
          DB_COLLECTION.CHESTS,
          e.chest,
        ].join('/')
        const { doc, getDoc, fireStore } = await getFirebase()
        const chestDataRef = doc(fireStore, chestPath)
        chestData = (await getDoc(chestDataRef)).data()
      }

      const key = `${e.elevator}#${e.room}#${e.chest}`
      if (!chestIdMapped[key]) return
      const path = `${ROUTES.MATH_VAULT}/${chestIdMapped[key]}/${chestData?.nextLevel || 1}/${
        chestData?.nextQuestion || 1
      }`
      dispatch(setCurrentLocationCreator({ room: e.location, chest: e.chest }))
      router.push(path)
    },
    [userId, chestIdMapped, dispatch, router],
  )

  const getChestSummary = useCallback(() => {
    const chestSummary: Record<string, unknown> = {}

    if (userId) {
      getFirebase()
        .then(({ getDocs, collection, fireStore }) =>
          getDocs(collection(fireStore, DB_COLLECTION.MATH_VAULT, String(userId), DB_COLLECTION.PLAY_CANVAS)).then(
            (querySnapshot) => {
              querySnapshot.forEach((_doc) => {
                chestSummary[_doc.id] = _doc.data()
              })
            },
          ),
        )
        .then(() => sendMessage({ chests: chestSummary }))
    }
  }, [userId, sendMessage])

  useEffect(() => {
    if (isReady) getChestSummary()
  }, [getChestSummary, isReady])

  const messageHandler = useCallback(
    (event) => {
      if (event.origin === appConfig.playcanvas.mathVault.origin) {
        playCanvasAnalytics.logPlayCanvasEvent(event.data)
        // check that the message is coming from playcanvas
        const data = event.data
        if (data.type === 'open-chest') {
          handleEvent(data)
        } else if (data.type === 'request' && data.request === 'location') {
          setIsReady(true)
          sendMessage({
            location: mathVaultLocation.room,
            chest: mathVaultLocation.chest,
            language,
            locales: playLocales,
            mappedChestIds: chestIdMapped,
          })
        } else if (data.type === 'sound') {
          switch (data.action) {
            case 'getGlobalSoundSettings':
              sendMessage({
                type: 'sound',
                action: 'setGlobalSoundSettings',
                value: { muted: globallyMuted, volume: globalVolume },
              })
              break
          }
        }
        if (typeof data.showBackButton === 'boolean') {
          setShowBackButton(data.showBackButton)
        }
      }
    },
    [
      handleEvent,
      sendMessage,
      mathVaultLocation,
      language,
      playLocales,
      chestIdMapped,
      globallyMuted,
      globalVolume,
      setShowBackButton,
    ],
  )

  useEffect(() => {
    sendMessage({
      type: 'sound',
      action: 'setGlobalSoundSettings',
      value: { muted: globallyMuted, volume: globalVolume },
    })
  }, [sendMessage, globallyMuted, globalVolume])

  useEffect(() => {
    window.addEventListener('message', messageHandler)

    return () => window.removeEventListener('message', messageHandler)
  }, [messageHandler])

  return (
    <div className="relative -mt-4">
      <PortalMuteButton
        className="absolute top-8 left-8"
        muteElement={<MuteElement />}
        unmuteElement={<UnMuteElement />}
      />
      <iframe
        className="vaultFrame w-full"
        src={appConfig.playcanvas.mathVault.href}
        style={{ height: height > 0 ? `calc(${height}px - ${br < Breakpoint.MD ? '36' : '62'}px)` : '90vh' }}
      />
    </div>
  )
}
